import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, reportingAPI } from '../services/api';
import toast from 'react-hot-toast';
import ReportList from './ReportList';
import ReportGeneration from './ReportGeneration';

const AdminReportsManagement = () => {
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showGenerateReport, setShowGenerateReport] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchCandidates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCandidates(currentPage, 20, searchTerm);
      const newCandidates = response.data.candidates || [];
      setCandidates(newCandidates);
      setTotalPages(response.data.total_pages || 1);
    } catch (err) {
      console.error('Error fetching candidates:', err);
      toast.error('Failed to load candidates');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  // Preserve selectedCandidate state - only clear when explicitly needed
  // This ensures the candidate view stays open during report operations

  const handleGenerateReport = (candidateId) => {
    setSelectedCandidate(candidateId);
    setShowGenerateReport(true);
  };

  const handleReportGenerated = (reportData) => {
    toast.success('Report generation started!');
    setShowGenerateReport(false);
    // Keep candidate selected so reports remain visible
    // setSelectedCandidate(null);
  };

  // Prevent candidate from collapsing when clicking inside ReportList
  const handleReportListClick = (e) => {
    e.stopPropagation();
  };

  const formatDate = (dateString) => {
    if (!dateString || dateString === '' || dateString === 'N/A') return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Report Management</h2>
          <p className="text-gray-600">Generate and manage AI-powered reports for candidates</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search candidates by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <button
            onClick={fetchCandidates}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Search
          </button>
        </div>
      </div>

      {/* Candidates List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Candidates</h3>
          <p className="text-gray-600">Select a candidate to view their reports or generate new ones</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading candidates...</p>
            </div>
          </div>
        ) : candidates.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">👥</div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Candidates Found</h3>
            <p className="text-gray-600">
              {searchTerm ? 'No candidates match your search criteria.' : 'No candidates have been registered yet.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {candidates.map((candidate) => (
              <div
                key={candidate.id}
                className={`p-6 hover:bg-gray-50 transition-colors cursor-pointer ${
                  selectedCandidate === candidate.id ? 'bg-primary-50 border-l-4 border-primary-500' : ''
                }`}
                onClick={() => setSelectedCandidate(selectedCandidate === candidate.id ? null : candidate.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-semibold text-lg">
                        {candidate.first_name?.charAt(0)}{candidate.last_name?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {candidate.first_name} {candidate.last_name}
                      </h4>
                      <p className="text-gray-600">{candidate.email}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span>Joined: {formatDate(candidate.createdAt)}</span>
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          candidate.isActive === true
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {candidate.isActive === true ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenerateReport(candidate.id);
                      }}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Generate Report
                    </button>
                    
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        selectedCandidate === candidate.id ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Expanded Content - Reports */}
                {selectedCandidate === candidate.id && (
                  <div 
                    className="mt-6 pt-6 border-t border-gray-200"
                    onClick={handleReportListClick}
                  >
                    <ReportList
                      candidateId={candidate.id}
                      showGenerateButton={false}
                      showHeader={false}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Report Generation Modal */}
      {showGenerateReport && selectedCandidate && (
        <ReportGeneration
          candidateId={selectedCandidate}
          onClose={() => {
            setShowGenerateReport(false);
            setSelectedCandidate(null);
          }}
          onReportGenerated={handleReportGenerated}
        />
      )}
    </div>
  );
};

export default AdminReportsManagement;
